<script>
import api from "@/helpers/api";
import { firstPhone } from "@/helpers/phones";

export default {
  name: "SiteFooter",
  data() {
    return {
      contact: {},
      settings: {},
      showBackToTop: false,
    };
  },
  computed: {
    year() { return new Date().getFullYear(); },
    primaryPhone() { return firstPhone(this.contact.phones); },
    publicBase() { return process.env.BASE_URL || "/"; },
  },
  async mounted() {
    await Promise.all([this.loadContact(), this.loadSettings()]);
    window.addEventListener("scroll", this.handleScroll, { passive: true });
    this.handleScroll();
  },
  beforeUnmount() {
    window.removeEventListener("scroll", this.handleScroll);
  },
  methods: {
    handleScroll() {
      this.showBackToTop = (window.scrollY || document.documentElement.scrollTop) > 240;
    },
    scrollToTop() {
      window.scrollTo({ top: 0, behavior: "smooth" });
    },
    async loadContact() {
      try {
        const { data } = await api.get("/public/jc/contact-info");
        this.contact = data || {};
      } catch (err) { console.warn("Contact load failed", err); }
    },
    async loadSettings() {
      try {
        const { data } = await api.get("/public/jc/site-settings");
        this.settings = data || {};
      } catch (err) { console.warn("Settings load failed", err); }
    },
  },
};
</script>

<template>
  <footer class="site-footer">
    <div class="container">
      <div class="row g-4 align-items-center">
        <div class="col-lg-5 col-md-6">
          <router-link to="/" class="footer-brand d-inline-block mb-3">
            <img :src="`${publicBase}images/logo.png`" :alt="settings.siteTitle || 'Sunhera Job'" />
          </router-link>
          <div class="socials-round">
            <a v-if="contact.facebookUrl" :href="contact.facebookUrl" target="_blank" rel="noopener" aria-label="Facebook"><i class="ri-facebook-fill"></i></a>
            <a v-if="contact.linkedInUrl" :href="contact.linkedInUrl" target="_blank" rel="noopener" aria-label="LinkedIn"><i class="ri-linkedin-fill"></i></a>
            <a v-if="contact.instagramUrl" :href="contact.instagramUrl" target="_blank" rel="noopener" aria-label="Instagram"><i class="ri-instagram-line"></i></a>
            <a v-if="contact.googleUrl" :href="contact.googleUrl" target="_blank" rel="noopener" aria-label="Google"><i class="ri-google-fill"></i></a>
          </div>
        </div>

        <div class="col-lg-7 col-md-6">
          <ul class="footer-contact list-unstyled m-0">
            <li v-if="contact.address"><span class="lbl">Address:</span> {{ contact.address }}{{ contact.city ? ", " + contact.city : "" }}{{ contact.state ? ", " + contact.state : "" }}</li>
            <li v-if="contact.email"><span class="lbl">Email:</span> <a :href="`mailto:${contact.email}`">{{ contact.email }}</a></li>
            <li v-if="primaryPhone"><span class="lbl">Call:</span><a :href="`tel:${primaryPhone}`">{{ primaryPhone }}</a></li>
          </ul>
        </div>
      </div>
    </div>

    <div class="footer-bottom text-center">
      <div class="container">
        &copy; Copyright {{ year }} By
        <i class="ri-heart-fill heart" aria-label="love"></i>
        <router-link to="/" class="brand-link">{{ settings.siteTitle || "Sunhera Job" }}</router-link>
        All Rights Reserved.
      </div>
    </div>

    <button
      type="button"
      class="back-to-top"
      :class="{ 'is-visible': showBackToTop }"
      aria-label="Back to top"
      @click="scrollToTop">
      <i class="ri-arrow-up-line"></i>
    </button>
  </footer>
</template>

<style scoped>
.footer-brand img { max-height: 52px; }
.socials-round { display: flex; gap: 12px; }
.socials-round a {
  width: 38px; height: 38px;
  border-radius: 50%;
  background: #fff;
  color: #2c2c2c;
  display: inline-flex; align-items: center; justify-content: center;
  font-size: 1.1rem;
  transition: transform 0.15s, background-color 0.15s, color 0.15s;
  text-decoration: none;
}
.socials-round a:hover {
  background: var(--sj-primary);
  color: #fff;
  transform: translateY(-2px);
}
.footer-contact { color: #d0d4dc; font-size: 0.95rem; line-height: 2; }
.footer-contact .lbl { color: #fff; font-weight: 600; margin-right: 6px; }
.footer-contact a { color: #d0d4dc; }
.footer-contact a:hover { color: var(--sj-primary); text-decoration: none; }
</style>
